import React, { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, ChevronLeft, ChevronRight, Download, Loader2, Search } from "lucide-react";
import RuleChips from "./RuleChips";
import SkuDetail from "./SkuDetail";
import { getCell, exportCell } from "../../lib/classificationApi";
import { formatPeso, formatNumber, cn } from "../../lib/utils";

const PAGE = 50;
const COLS = [
  ["sku", "SKU · name · category", "left"], ["annual_value", "Annual value", "right"], ["share", "Share", "right"],
  ["cv_raw", "Raw CV", "right"], ["cv_processed", "Proc. CV", "right"], ["final_class", "Final class", "center"],
  ["lead_time", "Lead time", "right"], ["risk_score", "Risk", "right"], ["ss_days", "SS days", "right"],
  ["doh", "DoH", "right"], ["action", "AI action", "left"],
];
const TONE = { critical: "text-critical", warning: "text-warning", success: "text-success", info: "text-info" };
const n = (v, d = 2) => (v === null || v === undefined ? "—" : Number(v).toFixed(d));

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

function useNarrow() {
  const query = "(max-width: 767px)";
  const [m, setM] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const f = (e) => setM(e.matches);
    mq.addEventListener("change", f);
    return () => mq.removeEventListener("change", f);
  }, []);
  return m;
}

// Phone layout: one stacked card per SKU instead of an 11-column table.
function Card({ r, open, onToggle, branch }) {
  const tid = `sku-row-${r.sku}`;
  const stats = [
    ["Annual value", formatPeso(r.annual_value, { compact: true })], ["Share", `${(r.share * 100).toFixed(2)}%`],
    ["Raw CV", n(r.cv_raw)], ["Proc. CV", n(r.cv_processed)], ["Lead time", `${r.lead_time}d`],
    ["Risk", n(r.risk_score)], ["SS days", n(r.ss_days, 1)], ["DoH", n(r.doh, 0)],
  ];
  return (
    <div className="border-t border-border">
      <div role="button" tabIndex={0} data-testid={tid} onClick={onToggle} onKeyDown={e => e.key === "Enter" && onToggle()}
           className={cn("px-4 py-3 cursor-pointer transition-colors", open ? "bg-primary/5" : "active:bg-surface-2/50")}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono-tabular text-[10.5px] text-muted break-all">{r.sku}</div>
            <div className="text-[12.5px] font-medium leading-snug">{r.name}</div>
            <div className="text-[10px] text-subtle">{r.category_label} · {r.family} · {r.supply_label}</div>
          </div>
          <div className="text-right shrink-0 flex items-start gap-1.5">
            <div>
              <div className="font-mono-tabular font-semibold text-[13px]" data-testid={`${tid}-class`}>{r.final_class}</div>
              {r.weather_reclassified && <div className="text-[9.5px] text-subtle font-mono-tabular">was {r.abc}{r.xyz_original}</div>}
            </div>
            <ChevronDown size={13} className={cn("mt-0.5 text-muted transition-transform", open && "rotate-180 text-primary")} />
          </div>
        </div>
        <RuleChips row={r} testid={`${tid}-chips`} />
        <dl className="grid grid-cols-4 gap-x-2 gap-y-1.5 mt-2.5">
          {stats.map(([l, v]) => (
            <div key={l} className="min-w-0">
              <dt className="text-[8.5px] uppercase tracking-wider text-subtle truncate">{l}</dt>
              <dd className="font-mono-tabular text-[11.5px]">{v}</dd>
            </div>
          ))}
        </dl>
        <div className={cn("mt-2 text-[11px] leading-snug", TONE[r.action_tone])}>{r.action}</div>
      </div>
      {open && <div data-testid={`${tid}-detail`} className="border-t border-primary/30"><SkuDetail sku={r.sku} branch={branch} /></div>}
    </div>
  );
}

function MobileSort({ sort, setSort }) {
  return (
    <div className="flex items-center gap-1.5 w-full" data-testid="sku-sort-mobile">
      <span className="text-[10px] uppercase tracking-widest text-muted">Sort</span>
      <select data-testid="sku-sort-select" value={sort.key} onChange={e => setSort(s => ({ ...s, key: e.target.value }))}
              className="flex-1 bg-bg border border-border rounded-sm px-2 py-1.5 text-[12px] focus:outline-none focus:border-primary">
        {COLS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
      </select>
      <button data-testid="sku-sort-dir" onClick={() => setSort(s => ({ ...s, dir: s.dir === "desc" ? "asc" : "desc" }))}
              className="w-8 h-8 border border-border rounded-sm inline-flex items-center justify-center hover:border-primary hover:text-primary transition-colors">
        {sort.dir === "desc" ? <ArrowDown size={12} /> : <ArrowUp size={12} />}
      </button>
    </div>
  );
}

function Row({ r, open, onToggle, colSpan, branch }) {
  const tid = `sku-row-${r.sku}`;
  return (
    <>
      <tr data-testid={tid} onClick={onToggle}
          className={cn("border-t border-border cursor-pointer align-top transition-colors", open ? "bg-primary/5" : "hover:bg-surface-2/50")}>
        <td className="py-2 px-3 min-w-[260px]">
          <div className="flex items-start gap-1.5">
            <ChevronDown size={12} className={cn("mt-0.5 text-muted shrink-0 transition-transform", open && "rotate-180 text-primary")} />
            <div className="min-w-0">
              <div className="font-mono-tabular text-[10.5px] text-muted">{r.sku}</div>
              <div className="text-[12px] font-medium leading-snug">{r.name}</div>
              <div className="text-[10px] text-subtle">{r.category_label} · {r.family}</div>
              <RuleChips row={r} testid={`${tid}-chips`} />
            </div>
          </div>
        </td>
        <td className="py-2 px-3 text-right font-mono-tabular">{formatPeso(r.annual_value, { compact: true })}</td>
        <td className="py-2 px-3 text-right font-mono-tabular text-muted">{(r.share * 100).toFixed(2)}%</td>
        <td className="py-2 px-3 text-right font-mono-tabular text-muted">{n(r.cv_raw)}</td>
        <td className="py-2 px-3 text-right font-mono-tabular">{n(r.cv_processed)}</td>
        <td className="py-2 px-3 text-center">
          <span className="font-mono-tabular font-semibold" data-testid={`${tid}-class`}>{r.final_class}</span>
          {r.weather_reclassified && <div className="text-[9.5px] text-subtle font-mono-tabular">was {r.abc}{r.xyz_original}</div>}
        </td>
        <td className="py-2 px-3 text-right font-mono-tabular whitespace-nowrap">{r.lead_time}d<div className="text-[9.5px] text-subtle font-sans">{r.supply_label}</div></td>
        <td className="py-2 px-3 text-right font-mono-tabular font-semibold">{n(r.risk_score)}</td>
        <td className="py-2 px-3 text-right font-mono-tabular">{n(r.ss_days, 1)}</td>
        <td className="py-2 px-3 text-right font-mono-tabular">{n(r.doh, 0)}</td>
        <td className={cn("py-2 px-3 text-[11px] leading-snug min-w-[190px]", TONE[r.action_tone])}>{r.action}</td>
      </tr>
      {open && <tr data-testid={`${tid}-detail`}><td colSpan={colSpan} className="p-0 border-t border-primary/30"><SkuDetail sku={r.sku} branch={branch} /></td></tr>}
    </>
  );
}

// Every SKU in the cell: search (incl. legacy part numbers), sortable columns, 50/page, full-cell CSV export.
export default function SkuTable({ code, branch }) {
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 300);
  const [sort, setSort] = useState({ key: "annual_value", dir: "desc" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [exporting, setExporting] = useState(false);
  const narrow = useNarrow();

  useEffect(() => { setPage(1); }, [dq, sort, code, branch]);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    getCell(code, { q: dq, sort: sort.key, dir: sort.dir, page, size: PAGE, branch })
      .then(d => { if (alive) { setData(d); setError(null); setOpen(null); } })
      .catch(e => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [code, dq, sort, page, branch]);

  const toggleSort = (key) => setSort(s => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
  async function onExport() {
    setExporting(true);
    try { await exportCell(code, { q: dq, sort: sort.key, dir: sort.dir, branch }); } catch (e) { setError(e.message); }
    setExporting(false);
  }
  const from = data ? (data.page - 1) * PAGE + 1 : 0;
  const to = data ? Math.min(data.total, data.page * PAGE) : 0;

  return (
    <div className="tile" data-testid="sku-table">
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-border">
        <span className="text-[11px] font-semibold tracking-widest uppercase text-muted mr-2">All SKUs in {code === "ZI" ? "Z-Intermittent" : code}</span>
        <div className="relative basis-full md:basis-auto flex-1 md:min-w-[200px] md:max-w-[360px] order-last md:order-none">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
          <input data-testid="sku-search" value={q} onChange={e => setQ(e.target.value)}
                 placeholder={narrow ? "SKU, name or legacy part no." : "Search SKU code, name or legacy part number"}
                 className="w-full bg-bg border border-border pl-7 pr-3 py-1.5 rounded-sm text-[12px] focus:outline-none focus:border-primary transition-colors" />
        </div>
        {loading && <Loader2 size={13} className="animate-spin text-muted" />}
        <span className="text-[11px] text-muted font-mono-tabular ml-auto" data-testid="sku-total">{data ? `${formatNumber(data.total)} SKUs` : ""}</span>
        <button data-testid="sku-export" onClick={onExport} disabled={exporting || !data?.total}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] border border-border hover:border-primary hover:text-primary rounded-sm disabled:opacity-40 transition-colors">
          {exporting ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} {narrow ? "Export CSV" : "Export whole cell (CSV)"}
        </button>
        {narrow && <div className="order-last basis-full"><MobileSort sort={sort} setSort={setSort} /></div>}
      </div>
      {error && <div data-testid="sku-table-error" className="m-4 text-[12px] text-critical">{error}</div>}
      {narrow ? (
        <div data-testid="sku-card-list">
          {data?.rows.map(r => <Card key={r.sku} r={r} branch={branch} open={open === r.sku} onToggle={() => setOpen(o => (o === r.sku ? null : r.sku))} />)}
          {data && data.rows.length === 0 && <div data-testid="sku-table-empty" className="py-10 text-center text-muted text-[12px]">No SKUs match “{dq}”.</div>}
        </div>
      ) : (
      <div className="overflow-auto max-h-[68vh]">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 z-10 bg-surface shadow-[0_1px_0_rgb(var(--border))]">
            <tr className="text-[9.5px] uppercase tracking-widest text-muted">
              {COLS.map(([key, label, align]) => (
                <th key={key} className={cn("py-2 px-3 font-semibold whitespace-nowrap", `text-${align}`)}>
                  <button data-testid={`sku-sort-${key}`} onClick={() => toggleSort(key)}
                          className={cn("inline-flex items-center gap-1 uppercase tracking-widest hover:text-primary", sort.key === key && "text-primary")}>
                    {label}{sort.key === key && (sort.dir === "desc" ? <ArrowDown size={9} /> : <ArrowUp size={9} />)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.rows.map(r => <Row key={r.sku} r={r} branch={branch} colSpan={COLS.length} open={open === r.sku} onToggle={() => setOpen(o => (o === r.sku ? null : r.sku))} />)}
            {data && data.rows.length === 0 && (
              <tr><td colSpan={COLS.length} data-testid="sku-table-empty" className="py-10 text-center text-muted text-[12px]">No SKUs match “{dq}”.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}
      {data && data.total > 0 && (
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-border text-[11px] text-muted">
          <span className="font-mono-tabular" data-testid="sku-range">Rows {formatNumber(from)}–{formatNumber(to)} of {formatNumber(data.total)}</span>
          <div className="flex items-center gap-2">
            <button data-testid="sku-page-prev" disabled={data.page <= 1} onClick={() => setPage(p => p - 1)}
                    className="w-7 h-7 border border-border rounded-sm inline-flex items-center justify-center hover:border-primary disabled:opacity-30"><ChevronLeft size={12} /></button>
            <span className="font-mono-tabular" data-testid="sku-page">Page {data.page} / {data.pages}</span>
            <button data-testid="sku-page-next" disabled={data.page >= data.pages} onClick={() => setPage(p => p + 1)}
                    className="w-7 h-7 border border-border rounded-sm inline-flex items-center justify-center hover:border-primary disabled:opacity-30"><ChevronRight size={12} /></button>
          </div>
        </div>
      )}
    </div>
  );
}
